// T113 (prośba księgowości 15.10): „Kadry” — stawki wszystkich (tylko podgląd) i pula urlopu do ustawienia.
// Dla uprawnienia „Kadry i księgowość” (canAccounting) i admina. Bez danych osobowych — tylko to, co potrzebne do umów i płac.
import React, { useState, useEffect, useCallback } from 'react';
import { PersonCell, RoleBadge, fullName, useToast, useSort, SortTh, ROLE_LABELS } from '../components/UI';
import api from '../utils/api';

const SORT = {
  employee: u => `${u.lastName} ${u.firstName}`,
  role: u => u.role,
  pool: u => u.hasLeaveEntitlement ? u.leaveDaysTotal : -1,
};
const zl = (v) => `${Number(v).toFixed(2).replace('.', ',').replace(',00', '')} zł`;

/** Stawki, które dotyczą tej osoby (jak WorkKinds w Pages.js): [etykieta, własna, domyślna, jednostka]. */
function ratesOf(u, d) {
  const out = [];
  const wh = u.role === 'WAREHOUSE' || u.warehouseWorkEnabled;
  const ev = u.role === 'EVENT' || u.eventWorkEnabled;
  if (wh) out.push([u.role === 'WAREHOUSE' ? 'Nadgodziny mag.' : 'Magazyn', u.rateWarehouse, d.rateWarehouse, '/h'], ['Nocna', u.rateNight, d.rateNight, '/h']);
  if (ev) out.push(['Event', u.rateEvent, d.rateEvent, '/h']);
  if (ev && u.animationWorkEnabled) out.push(['Animacja', u.rateAnimation, d.rateAnimation, '/h'], ['Animacja dniówka', u.rateAnimationDaily, d.rateAnimationDaily, '']); // T143
  if (ev) out.push(['Dniówka', u.rateEventDaily, d.rateEventDaily, '']); // T135
  return out;
}

export default function StaffPage() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [edit, setEdit] = useState(null); // { id, value }
  const { show, ToastComponent } = useToast();

  const load = useCallback(() => api.getStaff().then(setData).catch(e => show(e.message, 'error')), []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const d = data?.defaultRates || {};
  const rows = (data?.users || []).filter(u => (!role || u.role === role) && (!q || fullName(u).toLowerCase().includes(q.toLowerCase())));
  const ss = useSort(rows, SORT, 'employee', 'asc');

  const savePool = async () => {
    try { await api.setLeavePool(edit.id, Number(edit.value)); show('Pula urlopu zapisana'); setEdit(null); load(); }
    catch (e) { show(e.message, 'error'); }
  };

  const exportCsv = () => {
    const rateTxt = (u, label) => { const r = ratesOf(u, d).find(x => x[0] === label || (label === 'Magazyn' && x[0] === 'Nadgodziny mag.')); return r ? String(r[1] ?? r[2] ?? '').replace('.', ',') : ''; };
    const out = [['Pracownik', 'Rola', 'Stanowisko', 'Zatrudnienie', 'Przełożony', 'Magazyn / nadgodziny (zł/h)', 'Nocna (zł/h)', 'Event (zł/h)', 'Dniówka (zł)', 'Pula urlopu (dni)', 'Wykorzystane (dni)']];
    ss.sorted.forEach(u => out.push([fullName(u), ROLE_LABELS[u.role] || u.role, u.position || '', u.employmentType === 'HOURLY' ? 'Godziny' : 'Etat',
      u.supervisor ? fullName(u.supervisor) : '', rateTxt(u, 'Magazyn'), rateTxt(u, 'Nocna'), rateTxt(u, 'Event'), rateTxt(u, 'Dniówka'),
      u.hasLeaveEntitlement ? u.leaveDaysTotal : '', u.hasLeaveEntitlement ? u.leaveDaysUsed : '']));
    const csv = '﻿' + out.map(r => r.map(v => '"' + String(v).replace(/"/g, '""') + '"').join(';')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `kadry_${new Date().toLocaleDateString('sv-SE')}.csv`; a.click();
  };

  return (
    <div className="flex flex-col gap-14">
      <div className="page-header">
        <h2 className="page-title">Kadry — stawki i pule urlopu</h2>
        <button className="btn btn-outline" onClick={exportCsv} disabled={!data}>⬇ Eksport (Excel)</button>
      </div>
      <div className="card">
        <div className="flex gap-8 flex-wrap" style={{ alignItems: 'center', marginBottom: 10 }}>
          <input placeholder="Szukaj pracownika…" value={q} onChange={e => setQ(e.target.value)} style={{ maxWidth: 220 }} />
          <div className="filter-pills">
            {[['', 'Wszyscy'], ['WAREHOUSE', 'Fizyczni'], ['EVENT', 'Eventowi'], ['ADMINISTRATION', 'Biuro'], ['COORDINATOR', 'Koordynatorzy']].map(([v, l]) => (
              <button key={v} className={`filter-pill ${role === v ? 'active' : ''}`} onClick={() => setRole(v)}>{l}</button>
            ))}
          </div>
        </div>
        <div style={{ fontSize: 10, color: '#94A3B8', marginBottom: 8 }}>
          Stawki tylko do podglądu — zmienia je admin w profilu pracownika. Szara stawka = domyślna z Ustawień (pracownik nie ma własnej).
          Pulę urlopu (ile dni przysługuje w roku) ustawiasz tutaj ✎.
        </div>
        {!data ? <p style={{ color: '#94A3B8', padding: 20 }}>Ładowanie…</p> : (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr>
                <SortTh label="Pracownik" k="employee" sort={ss.sort} onSort={ss.toggle} />
                <SortTh label="Rola" k="role" sort={ss.sort} onSort={ss.toggle} />
                <th>Zatrudnienie</th><th>Przełożony</th><th>Stawki</th>
                <SortTh label="Pula urlopu" k="pool" sort={ss.sort} onSort={ss.toggle} />
              </tr></thead>
              <tbody>
                {ss.sorted.map(u => (
                  <tr key={u.id}>
                    <td><PersonCell name={fullName(u)} />{u.position && <div style={{ fontSize: 10, color: '#64748B', marginLeft: 29 }}>{u.position}</div>}</td>
                    <td><RoleBadge role={u.role} /></td>
                    <td style={{ fontSize: 11 }}>{u.employmentType === 'HOURLY' ? 'Godziny' : 'Etat'}</td>
                    <td style={{ fontSize: 11 }}>{u.supervisor ? fullName(u.supervisor) : <span style={{ color: u.role === 'ADMINISTRATION' ? '#DC2626' : '#CBD5E1' }}>{u.role === 'ADMINISTRATION' ? '⚠ brak' : '—'}</span>}</td>
                    <td style={{ fontSize: 11 }}>
                      {ratesOf(u, d).map(([label, own, def, unit]) => (
                        <div key={label}>{label}: {own != null
                          ? <b>{zl(own)}{unit}</b>
                          : <span style={{ color: '#94A3B8' }} title="Domyślna stawka z Ustawień">{def != null ? `${zl(def)}${unit}` : '—'} (dom.)</span>}</div>
                      ))}
                      {!ratesOf(u, d).length && <span style={{ color: '#CBD5E1' }}>— (pensja poza aplikacją)</span>}
                    </td>
                    <td style={{ fontSize: 11 }}>
                      {!u.hasLeaveEntitlement ? <span style={{ color: '#CBD5E1' }}>bez urlopu</span>
                        : edit?.id === u.id ? (
                          <div className="flex gap-4" style={{ alignItems: 'center' }}>
                            <input type="number" min="0" max="60" value={edit.value} onChange={e => setEdit({ ...edit, value: e.target.value })} style={{ width: 60 }} autoFocus
                              onKeyDown={e => { if (e.key === 'Enter') savePool(); if (e.key === 'Escape') setEdit(null); }} /> dni
                            <button className="btn btn-primary btn-sm" onClick={savePool}>Zapisz</button>
                            <button className="btn btn-ghost btn-sm" onClick={() => setEdit(null)}>✕</button>
                          </div>
                        ) : (
                          <span><b>{u.leaveDaysTotal}</b> dni <span style={{ color: '#94A3B8' }}>(wykorzystane {u.leaveDaysUsed}{u.leaveHoursUsed ? ` dni ${u.leaveHoursUsed} h` : ''})</span>{' '}
                            <button className="btn btn-ghost btn-sm" title="Zmień pulę urlopu" onClick={() => setEdit({ id: u.id, value: String(u.leaveDaysTotal) })}>✎</button></span>
                        )}
                    </td>
                  </tr>
                ))}
                {ss.sorted.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94A3B8', padding: 20 }}>Brak pracowników</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {ToastComponent}
    </div>
  );
}
